import React, { useRef, useEffect, useState, useCallback } from "react";
import { Play, Pause } from "lucide-react";

const LS_NAME = "audioMessageRate";
// Only these are ever produced by the speed toggle. Guards against a stored
// "0" (or any other corrupted value) silently freezing playback: "0" is a
// non-empty string, so `getItem() || "1"` does NOT fall back to "1" for it —
// parseFloat("0") = 0, and playbackRate = 0 makes the player render normally
// (no errors) while never advancing or producing sound.
const VALID_RATES = [1, 1.5, 2];
const getStoredRate = () => {
  const stored = parseFloat(localStorage.getItem(LS_NAME));
  return VALID_RATES.includes(stored) ? stored : 1;
};

// Deterministic pseudo-random bar heights seeded from the url, so a given
// voice note always renders the same waveform shape (like WhatsApp) without
// decoding the audio.
const BAR_COUNT = 34;
const buildBars = seed => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const bars = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    // Bias toward mid heights with occasional peaks — reads as speech.
    bars.push(0.25 + ((h % 1000) / 1000) * 0.75);
  }
  return bars;
};

const fmt = secs => {
  if (!Number.isFinite(secs) || secs < 0) secs = 0;
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export default function Audio({ url, isLeft = false }) {
  const audioRef = useRef(null);
  const [bars] = useState(() => buildBars(url || "audio"));
  const [rate, setRate] = useState(getStoredRate);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  // Apply (validated) playback rate whenever it changes or the element loads.
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
    localStorage.setItem(LS_NAME, String(rate));
  }, [rate]);

  // A <source>'s src changing after the element loaded isn't picked up
  // automatically — .load() forces a re-fetch/re-decode of the new url.
  useEffect(() => {
    const el = audioRef.current;
    if (el) {
      el.load();
      setPlaying(false);
      setCurrent(0);
    }
  }, [url]);

  const handleLoadedMetadata = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    el.playbackRate = rate;
    // ogg/opus and webm recordings frequently report duration = Infinity
    // until the element is forced to seek to the end once. Without this the
    // progress bar and time never work and some browsers refuse to play.
    if (!Number.isFinite(el.duration)) {
      const onSeeked = () => {
        el.removeEventListener("timeupdate", onSeeked);
        el.currentTime = 0;
        setDuration(Number.isFinite(el.duration) ? el.duration : 0);
      };
      el.addEventListener("timeupdate", onSeeked);
      el.currentTime = 1e101;
    } else {
      setDuration(el.duration);
    }
  }, [rate]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.playbackRate = rate;
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  };

  const cycleRate = () => {
    setRate(r => VALID_RATES[(VALID_RATES.indexOf(r) + 1) % VALID_RATES.length] || 1);
  };

  const seekToFraction = fraction => {
    const el = audioRef.current;
    if (!el || !Number.isFinite(el.duration)) return;
    el.currentTime = Math.max(0, Math.min(1, fraction)) * el.duration;
  };

  const progress = duration > 0 ? current / duration : 0;

  // Color scheme adapts to the bubble it sits in: incoming bubbles are
  // white/slate, outgoing are indigo — so accents must flip for contrast.
  const accent = isLeft ? "bg-indigo-500" : "bg-white";
  const track = isLeft ? "bg-slate-300 dark:bg-slate-600" : "bg-white/35";
  const btn = isLeft
    ? "bg-indigo-500 text-white hover:bg-indigo-600"
    : "bg-white text-indigo-600 hover:bg-indigo-50";
  const timeText = isLeft ? "text-slate-500 dark:text-slate-400" : "text-indigo-100";
  const ratePill = isLeft
    ? "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300"
    : "bg-white/20 text-white hover:bg-white/30";

  return (
    <div className="flex items-center gap-2.5 py-1 min-w-[220px]">
      <audio
        ref={audioRef}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={() => setCurrent(audioRef.current?.currentTime || 0)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
          if (audioRef.current) audioRef.current.currentTime = 0;
        }}
        className="hidden"
      >
        <source src={url} />
      </audio>

      <button
        type="button"
        onClick={toggle}
        className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center shadow-sm transition-colors ${btn}`}
        aria-label={playing ? "Pausar" : "Reproducir"}
      >
        {playing ? (
          <Pause className="w-4 h-4" fill="currentColor" />
        ) : (
          <Play className="w-4 h-4 translate-x-[1px]" fill="currentColor" />
        )}
      </button>

      <div className="flex flex-col flex-1 gap-1">
        <div
          className="flex items-center gap-[2px] h-7 cursor-pointer"
          onClick={e => {
            const rect = e.currentTarget.getBoundingClientRect();
            seekToFraction((e.clientX - rect.left) / rect.width);
          }}
        >
          {bars.map((barHeight, i) => {
            const filled = i / BAR_COUNT <= progress;
            return (
              <span
                key={i}
                className={`flex-1 rounded-full transition-colors ${filled ? accent : track}`}
                style={{ height: `${Math.round(barHeight * 100)}%` }}
              />
            );
          })}
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-[10px] tabular-nums ${timeText}`}>
            {fmt(current)} / {fmt(duration)}
          </span>
          <button
            type="button"
            onClick={cycleRate}
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md transition-colors ${ratePill}`}
          >
            {rate}x
          </button>
        </div>
      </div>
    </div>
  );
}
